import { useState, useEffect, useMemo } from "react";
import { Icons, PRODUCT_ICON_MAP, PRODUCT_COLOR_MAP } from "./bookingConstants";
import s from "./StepAssignments.module.css";

/* ─── Constants ───────────────────────────────────────────── */

const PRODUCTS_PER_PAGE = 3;
const TRAVELERS_PER_PAGE = 10;

/* ─── Helpers ─────────────────────────────────────────────── */

function initials(first, last) {
  return `${(first || "")[0] || ""}${(last || "")[0] || ""}`.toUpperCase();
}

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function productLabel(p) {
  if (p.type === "Flight") {
    const leg = (p.legs || [])[0];
    if (leg?.airlineName) return leg.airlineName + (leg.airlineCode ? ` (${leg.airlineCode})` : "");
    return p.supplier || "";
  }
  if (p.type === "Hotel") return p.hotelName || p.hotelCity || p.supplier || "";
  if (p.type === "Transfer") return p.transferType ? `${p.transferType} Transfer` : "Airport Transfer";
  if (p.type === "Insurance") return p.insuranceType || "Travel Insurance";
  if (p.type === "CarRental") return p.carCategory || p.carType || "Car Rental";
  if (p.type === "Visa") return p.visaType || p.destination || "Visa";
  if (p.type === "Cruise") return p.cruiseLine || "Cruise";
  if (p.type === "Package") return p.packageName || "Package";
  return p.supplier || p.type;
}

function productBadge(p) {
  if (p.type === "Flight") {
    const n = (p.legs || []).length;
    if (p.flightType === "MF" || p.flightType === "Multi" || n > 2) return "Multi-City";
    if (p.flightType === "RT" || n === 2) return "Round Trip";
    if (p.flightType === "OW" || n === 1) return "One Way";
    return null;
  }
  if (p.type === "Hotel") return p.destination || p.city || null;
  if (p.type === "Transfer") {
    const a = p.pickupLocation || "", b = p.dropoffLocation || "";
    if (a && b) return `${a} → ${b}`;
    return p.transferMoment ? `${p.transferMoment} Transfer` : null;
  }
  return null;
}

const AVATAR_COLORS = [
  { bg: "#eef3ff", color: "#3b5bdb" },
  { bg: "#fff4e6", color: "#e67700" },
  { bg: "#e6fcf5", color: "#0d7a4f" },
  { bg: "#f3f0ff", color: "#6741d9" },
  { bg: "#fff0f6", color: "#c2255c" },
  { bg: "#e7f5ff", color: "#1971c2" },
];
const avatarColor = (i) => AVATAR_COLORS[i % AVATAR_COLORS.length];

/* ─── Pagination ──────────────────────────────────────────── */

const Pagination = ({ current, total, onPage, info }) => {
  if (total <= 1 && !info) return null;
  return (
    <div className={s.pgWrap}>
      {total > 1 && (
        <div className={s.pgRow}>
          <button className={s.pgArrow} disabled={current === 0} onClick={() => onPage(current - 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          {Array.from({ length: total }, (_, i) => (
            <button key={i} className={`${s.pgNum} ${i === current ? s.pgNumOn : ""}`} onClick={() => onPage(i)}>{i + 1}</button>
          ))}
          <button className={s.pgArrow} disabled={current >= total - 1} onClick={() => onPage(current + 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      )}
      {info && <div className={s.pgInfo}>{info}</div>}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   StepAssignments
   ═══════════════════════════════════════════════════════════ */

const StepAssignments = ({ data, onChange, onRemoveAssignment }) => {
  const [selPid, setSelPid] = useState(null);
  const [pPage, setPPage] = useState(0);
  const [search, setSearch] = useState("");
  const [aPage, setAPage] = useState(0);

  const products = data.products || [];
  const travelers = data.travelers || [];
  const assignments = data.assignments || [];

  useEffect(() => { if (!selPid && products.length) setSelPid(products[0].id); }, [products, selPid]);

  // product pagination
  const pTotal = Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));
  const pSafe = Math.min(Math.max(pPage, 0), pTotal - 1);
  const visProd = useMemo(() => products.slice(pSafe * PRODUCTS_PER_PAGE, pSafe * PRODUCTS_PER_PAGE + PRODUCTS_PER_PAGE), [products, pSafe]);

  // traveler search + pagination
  const filtered = useMemo(() => {
    const list = travelers.map((t, i) => ({ ...t, _i: i }));
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((t) => `${t.firstName} ${t.lastName}`.toLowerCase().includes(q));
  }, [travelers, search]);
  const aTotal = Math.max(1, Math.ceil(filtered.length / TRAVELERS_PER_PAGE));
  const aSafe = Math.min(Math.max(aPage, 0), aTotal - 1);
  const paged = useMemo(() => filtered.slice(aSafe * TRAVELERS_PER_PAGE, aSafe * TRAVELERS_PER_PAGE + TRAVELERS_PER_PAGE), [filtered, aSafe]);
  useEffect(() => { setAPage(0); }, [search]);

  // assignment logic
  const isOn = (ti, pid) => assignments.some((a) => a.travelerIndex === ti && a.productId === pid);
  const toggle = (ti, pid) => {
    const ex = assignments.find((a) => a.travelerIndex === ti && a.productId === pid);
    if (ex) { if (onRemoveAssignment) onRemoveAssignment(ex); else onChange("assignments", assignments.filter((a) => !(a.travelerIndex === ti && a.productId === pid))); }
    else onChange("assignments", [...assignments, { travelerIndex: ti, productId: pid }]);
  };
  const countFor = (pid) => assignments.filter((a) => a.productId === pid).length;
  const selPage = (pid) => {
    const ex = new Set(assignments.filter((a) => a.productId === pid).map((a) => a.travelerIndex));
    const add = paged.filter((t) => !ex.has(t._i)).map((t) => ({ travelerIndex: t._i, productId: pid }));
    if (add.length) onChange("assignments", [...assignments, ...add]);
  };
  const selAll = (pid) => {
    const ex = new Set(assignments.filter((a) => a.productId === pid).map((a) => a.travelerIndex));
    const add = travelers.map((_, i) => i).filter((i) => !ex.has(i)).map((i) => ({ travelerIndex: i, productId: pid }));
    if (add.length) onChange("assignments", [...assignments, ...add]);
  };
  const selEverything = () => {
    const ex = new Set(assignments.map((a) => `${a.travelerIndex}-${a.productId}`));
    const add = [];
    products.forEach((p) => { travelers.forEach((_, i) => { if (!ex.has(`${i}-${p.id}`)) add.push({ travelerIndex: i, productId: p.id }); }); });
    if (add.length) onChange("assignments", [...assignments, ...add]);
  };

  if (!products.length || !travelers.length) {
    return (
      <div className={s.wrap}>
        <div className={s.empty}>
          <div className={s.emptyIcon}>{Icons.link}</div>
          <div className={s.emptyH}>Products and travelers required</div>
          <div className={s.emptyP}>Add at least one product and one traveler before creating assignments.</div>
        </div>
      </div>
    );
  }

  const sel = products.find((p) => p.id === selPid) || products[0];
  const sColors = PRODUCT_COLOR_MAP[sel?.type] || { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
  const cnt = countFor(sel?.id);

  return (
    <div className={s.wrap}>
      {/* Title */}
      <div className={s.title}>
        <h2>TRAVELER – PRODUCT ASSIGNMENTS</h2>
        <p>Assign travelers to one or more products</p>
      </div>

      <div className={s.container}>
        {/* ═══ LEFT — TRAVELERS ═══ */}
        <div className={s.left}>
          <div className={s.leftHead}>
            <div className={s.leftHeadRow}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <span>TRAVELERS ({travelers.length})</span>
            </div>
            <p>All travelers in this booking</p>
          </div>

          <div className={s.leftList}>
            {travelers.map((t, i) => {
              const ac = avatarColor(i);
              return (
                <div key={i} className={s.leftRow}>
                  <span className={s.leftNum}>{i + 1}</span>
                  <span className={s.leftAvatar} style={{ background: ac.bg, color: ac.color }}>{initials(t.firstName, t.lastName)}</span>
                  <div className={s.leftInfo}>
                    <div className={s.leftName}>{t.firstName} <span>{(t.lastName || "").toUpperCase()}</span></div>
                    <div className={s.leftDob}>{fmtDate(t.dob)}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <Pagination current={0} total={1} onPage={() => {}} />

          <div className={s.leftFoot}>
            <button className={s.leftBtn} onClick={selEverything}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
              Select All Travelers
            </button>
          </div>
        </div>

        {/* ═══ RIGHT ═══ */}
        <div className={s.right}>
          {/* ── PRODUCT SELECTOR ── */}
          <div className={s.prodBlock}>
            <div className={s.prodHead}>
              <div className={s.prodHeadRow}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                <span>SELECT PRODUCT</span>
              </div>
              <p>Choose a product to assign travelers</p>
            </div>

            <div className={s.prodCards}>
              {visProd.map((p, idx) => {
                const c = PRODUCT_COLOR_MAP[p.type] || { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
                const on = p.id === sel?.id;
                const num = pSafe * PRODUCTS_PER_PAGE + idx + 1;
                const sub = productLabel(p);
                const badge = productBadge(p);
                return (
                  <div key={p.id} className={`${s.pCard} ${on ? s.pCardOn : ""}`} style={on ? { borderColor: c.color } : {}} onClick={() => setSelPid(p.id)}>
                    <div className={s.pCardTop}>
                      <div className={s.pCardIcon} style={{ background: c.bg, color: c.color }}>{PRODUCT_ICON_MAP[p.type] || Icons.box}</div>
                      <div className={s.pCardInfo}>
                        <div className={s.pCardType}>{num}. {p.type.toUpperCase()}</div>
                        {sub && <div className={s.pCardSub}>{sub}</div>}
                      </div>
                      <div className={`${s.pRadio} ${on ? s.pRadioOn : ""}`} style={on ? { borderColor: c.color } : {}}>
                        {on && <div className={s.pRadioDot} style={{ background: c.color }} />}
                      </div>
                    </div>
                    <div className={s.pCardBody}>
                      {p.supplier && <div className={s.pCardMeta}>Supplier: <strong>{p.supplier}</strong></div>}
                      {p.supplierRef && <div className={s.pCardMeta}>Booking Ref: <strong>{p.supplierRef}</strong></div>}
                    </div>
                    {badge && <div className={s.pCardBadge} style={{ background: c.bg, color: c.color }}>{badge}</div>}
                  </div>
                );
              })}
            </div>

            <Pagination current={pSafe} total={pTotal} onPage={setPPage} info={`Showing ${pSafe * PRODUCTS_PER_PAGE + 1} to ${Math.min((pSafe + 1) * PRODUCTS_PER_PAGE, products.length)} of ${products.length} products (${PRODUCTS_PER_PAGE} per page)`} />
          </div>

          {/* ── ASSIGN ── */}
          {sel && (
            <div className={s.assignBlock}>
              <div className={s.assignHead}>
                <div className={s.assignHeadL}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>
                  <span>ASSIGN TRAVELERS TO</span>
                  <div className={s.assignPill} style={{ background: sColors.bg, color: sColors.color, borderColor: sColors.border }}>
                    <span className={s.assignPillIcon} style={{ color: sColors.color }}>{PRODUCT_ICON_MAP[sel.type] || Icons.box}</span>
                    {products.indexOf(sel) + 1}. {sel.type.toUpperCase()} {productLabel(sel)}
                  </div>
                </div>
                <div className={s.assignCount} style={{ color: sColors.color }}>{cnt} of {travelers.length} selected</div>
              </div>
              <p className={s.assignSub}>Select the travelers that will use this product</p>

              <div className={s.assignToolbar}>
                <div className={s.searchWrap}>
                  <svg className={s.searchSvg} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input className={s.searchInput} placeholder="Search traveler..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <button className={s.tbBtn} onClick={() => selPage(sel.id)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Select All (Page)
                </button>
                <button className={s.tbBtnPrimary} onClick={() => selAll(sel.id)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>
                  Assign to ALL ({travelers.length} Travelers)
                </button>
              </div>

              <div className={s.assignList}>
                {paged.map((t) => {
                  const on = isOn(t._i, sel.id);
                  const ac = avatarColor(t._i);
                  return (
                    <div key={t._i} className={`${s.aRow} ${on ? s.aRowOn : ""}`} onClick={() => toggle(t._i, sel.id)}>
                      <div className={`${s.chk} ${on ? s.chkOn : ""}`}>
                        {on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <div className={s.aAvatar} style={{ background: ac.bg, color: ac.color }}>{initials(t.firstName, t.lastName)}</div>
                      <div className={s.aName}>{t.firstName} <span>{(t.lastName || "").toUpperCase()}</span></div>
                      <div className={s.aDob}>{fmtDate(t.dob)}</div>
                    </div>
                  );
                })}
                {!paged.length && <div className={s.noMatch}>No travelers match your search</div>}
              </div>

              <div className={s.assignFoot}>
                <Pagination current={aSafe} total={aTotal} onPage={setAPage} info={`Showing ${aSafe * TRAVELERS_PER_PAGE + 1} to ${Math.min((aSafe + 1) * TRAVELERS_PER_PAGE, filtered.length)} of ${filtered.length} travelers (${TRAVELERS_PER_PAGE} per page)`} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepAssignments;
